import { Component, Prop, State, Event, EventEmitter, h } from '@stencil/core';
import { assetUrl } from '../../../../../utils/helpers/asset-path';

const COMPONENT_NAME = 'dh-rating-stars';
const STAR_MASK = assetUrl('dochub/rating-star.svg');

/**
 * Star rating — display by default, optionally interactive for collecting a vote.
 * In display mode the row fills to `score` (with `step` snapping, e.g. `step="0.5"` →
 * full / half / empty). Set `readonly="false"` to let the user rate: hovering previews a
 * fill snapped to `step` (whole stars when no step), and clicking commits the value and
 * fires `ratingSelect`. Stars are drawn by masking `rating-star.svg`, so the track and
 * fill colours adapt to any background via CSS custom properties.
 *
 * @category Elements
 *
 * @example
 *   <dh-rating-stars score="4.5"></dh-rating-stars>
 *
 * @example
 *   <!-- half-star steps: 3.6 renders as 3.5 (full, full, full, half, empty) -->
 *   <dh-rating-stars score="3.6" step="0.5"></dh-rating-stars>
 *
 * @example
 *   <!-- interactive: hover previews half-star fill, click fires `ratingSelect` -->
 *   <dh-rating-stars score="3.6" step="0.5" readonly="false"></dh-rating-stars>
 */
@Component({
  tag: 'dh-rating-stars',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhRatingStars {
  /** Rating score from 0 to `max`; decimals render a partial last star. */
  @Prop() score: number = 0;

  /**
   * Number of stars rendered.
   * @default 5
   */
  @Prop() max: number = 5;

  /**
   * Star size in pixels.
   * @default 16
   */
  @Prop() size: number = 16;

  /**
   * Rounding step applied to `score` before filling, e.g. `0.5` snaps to the
   * nearest half star (3.6 → 3.5). `0` keeps the exact fractional fill. When
   * interactive (`readonly="false"`) it is also the granularity of hover/selection
   * (a positive `step < 1` enables half-star selection; otherwise whole stars).
   * @default 0
   */
  @Prop() step: number = 0;

  /**
   * Display-only when `true`. Set to `false` to let the user pick a rating (hover
   * preview + click). Network submission of the chosen value is left to the consumer
   * via the `ratingSelect` event.
   * @default true
   */
  @Prop() readonly: boolean = true;

  /** Accessible label. Defaults to "Rated {score} out of {max}". */
  @Prop() label?: string;

  /** Value previewed while the pointer hovers the stars (interactive only). */
  @State() hoverValue: number | null = null;

  /** Value the user has selected (interactive only); drives the fill once chosen. */
  @State() selectedValue: number | null = null;

  /** Fired when the user commits a rating (click, or Enter/Space). Detail = the value. */
  @Event() ratingSelect!: EventEmitter<number>;

  // Selection granularity: a positive sub-1 step enables half-star (etc.) picking;
  // anything else falls back to whole stars (matches the original whole-star vote).
  private get snapStep(): number {
    return this.step > 0 && this.step < 1 ? this.step : 1;
  }

  // Score actually rendered: hover/selection take precedence, else score snapped to step.
  // Hover never applies in readonly mode (e.g. after a vote locks the row) — only the
  // committed selection or the score shows.
  private get activeValue(): number {
    if (!this.readonly && this.hoverValue !== null) return this.hoverValue;
    if (this.selectedValue !== null) return this.selectedValue;
    return this.step > 0 ? Math.round(this.score / this.step) * this.step : this.score;
  }

  private fillWidth(starIndex: number): number {
    return Math.max(0, Math.min(100, (this.activeValue - starIndex) * 100));
  }

  // Map a pointer position within star `index` to a value snapped to `snapStep`.
  private valueFromPointer(index: number, event: MouseEvent): number {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const step = this.snapStep;
    const units = Math.min(1, Math.max(step, Math.ceil(fraction / step) * step));
    return index + units;
  }

  private handleStarMove = (index: number, event: MouseEvent) => {
    if (this.readonly) return;
    this.hoverValue = this.valueFromPointer(index, event);
  };

  private handleLeave = () => {
    this.hoverValue = null;
  };

  private handleStarClick = (index: number, event: MouseEvent) => {
    if (this.readonly) return;
    const value = this.valueFromPointer(index, event);
    this.selectedValue = value;
    this.ratingSelect.emit(value);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.readonly) return;
    const step = this.snapStep;
    const current = this.selectedValue ?? this.activeValue;

    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedValue = Math.min(this.max, current + step);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedValue = Math.max(step, current - step);
    } else if ((event.key === 'Enter' || event.key === ' ') && this.selectedValue !== null) {
      event.preventDefault();
      this.ratingSelect.emit(this.selectedValue);
    }
  };

  render() {
    const stars = Math.max(0, Math.round(this.max));
    const interactive = !this.readonly;
    const ariaLabel = this.label || `Rated ${this.score} out of ${this.max}`;

    const groupProps = interactive
      ? {
          role: 'slider',
          tabindex: 0,
          'aria-valuemin': 0,
          'aria-valuemax': this.max,
          'aria-valuenow': this.activeValue,
          'aria-label': this.label || `Rate out of ${this.max}`,
          onMouseLeave: this.handleLeave,
          onKeyDown: this.handleKeyDown,
        }
      : { role: 'img', 'aria-label': ariaLabel };

    return (
      <div
        class={{ [COMPONENT_NAME]: true, [`${COMPONENT_NAME}--interactive`]: interactive }}
        // Single quotes inside url(): the SSR style attribute is serialised with double
        // quotes and inner ones are not escaped, so `url("…")` truncates the attribute
        // and the mask is missing until hydration re-sets it via the DOM.
        style={{ '--_mask': `url('${STAR_MASK}')`, '--_size': `${this.size}px` } as Record<string, string>}
        {...groupProps}
      >
        {Array.from({ length: stars }).map((_, index) => {
          const fill = this.fillWidth(index);
          return (
            <span
              class={`${COMPONENT_NAME}__star`}
              aria-hidden="true"
              onMouseMove={interactive ? event => this.handleStarMove(index, event) : undefined}
              onClick={interactive ? event => this.handleStarClick(index, event) : undefined}
            >
              <span class={`${COMPONENT_NAME}__track`} />
              {fill > 0 && (
                <span class={`${COMPONENT_NAME}__fill-clip`} style={{ width: `${fill}%` }}>
                  <span class={`${COMPONENT_NAME}__fill-inner`} />
                </span>
              )}
            </span>
          );
        })}
      </div>
    );
  }
}
